import { useNavigate } from 'react-router-dom';
import { useProgress } from '@/context/ProgressContext';
import { SECTIONS } from '@/data/sections';
import { getAllItems } from '@/data';
import { ArrowLeft, Lock, CheckCircle2, Play, Star } from 'lucide-react';

export default function CourseMap() {
  const navigate = useNavigate();
  const { progress, isUnlocked, isCompleted, totalItems } = useProgress();
  const items = getAllItems();
  const completedCount = progress.completedItems.length;
  const percentage = Math.round((completedCount / totalItems) * 100);

  return (
    <div className="relative z-10 min-h-screen px-4 py-6 max-w-3xl mx-auto" data-testid="course-map">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => navigate('/')}
          className="text-[#A1A1AA] hover:text-white flex items-center gap-1 text-sm transition-colors"
          data-testid="back-to-home"
        >
          <ArrowLeft size={16} /> Home
        </button>
        <h1 className="text-xl font-bold font-outfit text-white">Course Map</h1>
        <span className="text-sm text-[#A1A1AA] font-quicksand">{percentage}%</span>
      </div>

      {/* Progress bar */}
      <div className="bg-white/10 h-2 rounded-full overflow-hidden mb-8">
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{
            width: `${percentage}%`,
            background: 'linear-gradient(to right, #FF0099, #FF5E00)',
          }}
        />
      </div>

      {/* Progress dots */}
      <div className="flex flex-wrap gap-1 mb-8 justify-center" data-testid="progress-dots">
        {items.map(item => (
          <div
            key={item.id}
            className={`w-2 h-2 rounded-full transition-colors ${
              isCompleted(item.id)
                ? 'bg-[#16FF00]'
                : isUnlocked(item.id)
                ? 'bg-[#1E90FF]'
                : 'bg-white/10'
            }`}
            title={item.title}
          />
        ))}
      </div>

      {/* Sections */}
      {SECTIONS.map((section, idx) => {
        const sectionItems = items.filter(i => i.sectionId === section.id);
        const sectionDone = sectionItems.filter(i => isCompleted(i.id)).length;
        const allDone = sectionDone === sectionItems.length && sectionItems.length > 0;

        return (
          <div
            key={section.id}
            className="mb-5 fade-in-up"
            style={{ animationDelay: `${idx * 0.04}s` }}
          >
            <div
              className={`bg-[#121225]/80 backdrop-blur-sm border rounded-2xl p-5 transition-all ${
                allDone ? 'border-[#16FF00]/30' : 'border-white/10'
              }`}
            >
              {/* Section header */}
              <div className="flex items-center justify-between mb-3">
                <div>
                  <span className="text-xs text-[#71717A] font-mono uppercase tracking-wider">
                    Section {section.id}
                  </span>
                  <h2 className="text-base md:text-lg font-bold font-outfit text-white">
                    {section.title}
                  </h2>
                </div>
                <span className={`text-sm font-mono ${allDone ? 'text-[#16FF00]' : 'text-[#A1A1AA]'}`}>
                  {sectionDone}/{sectionItems.length}
                </span>
              </div>

              {/* Lesson items */}
              <div className="space-y-2">
                {sectionItems.map(item => {
                  const completed = isCompleted(item.id);
                  const unlocked = isUnlocked(item.id);
                  const isMiniProject = item.type === 'mini-project';

                  return (
                    <button
                      key={item.id}
                      onClick={() => unlocked && navigate(`/lesson/${item.id}`)}
                      disabled={!unlocked}
                      className={`w-full flex items-center gap-3 p-3 rounded-xl text-left transition-all ${
                        completed
                          ? 'bg-[#16FF00]/5 border border-[#16FF00]/20'
                          : unlocked
                          ? 'bg-white/5 border border-white/10 hover:border-[#FF0099]/40 cursor-pointer'
                          : 'bg-white/[0.02] border border-white/5 opacity-40 cursor-not-allowed'
                      } ${isMiniProject ? 'border-l-4 !border-l-[#FFED00]' : ''}`}
                      data-testid={`lesson-item-${item.id}`}
                    >
                      {completed ? (
                        <CheckCircle2 size={18} className="text-[#16FF00] shrink-0" />
                      ) : unlocked ? (
                        <Play size={16} className="text-[#1E90FF] shrink-0" />
                      ) : (
                        <Lock size={14} className="text-[#71717A] shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <span
                          className={`text-sm font-medium font-quicksand ${
                            completed ? 'text-[#16FF00]' : unlocked ? 'text-white' : 'text-[#71717A]'
                          }`}
                        >
                          {isMiniProject ? 'Mini Project: ' : ''}
                          {item.title}
                        </span>
                      </div>
                      <span className="text-xs text-[#71717A] shrink-0 flex items-center gap-1">
                        <Star size={10} /> {item.xp}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
